import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import { DownOutlined } from '@ant-design/icons';
import { FEE_NOTICE_EFFECTIVE_DATE } from '../../constants/feeChangeNotice';
import { scrollToFeeNoticeBand } from '../../hooks/useFeeChangeNotice';
import { useMeasuredHeight } from '../../hooks/useMeasuredHeight';

export const FEE_CHANGE_BANNER_HEIGHT = 44;

const BannerBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2;
  min-height: ${FEE_CHANGE_BANNER_HEIGHT}px;
  background: #eaf4fb;
  border-bottom: 1px solid #cfe6f5;
  font-family: 'Euclid Circular A', sans-serif;
  display: flex;
  align-items: center;
`;

const BannerInner = styled.div`
  width: 95%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media screen and (min-width: 861px) {
    width: 80%;
    padding: 0 50px;
  }

  @media screen and (max-width: 860px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const BannerText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #1a202c;
`;

const Emphasis = styled.span`
  font-weight: 600;
  color: #045a8f;
`;

const BannerAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 0;
  padding: 2px 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #045a8f;
  white-space: nowrap;

  &:hover {
    color: #007ac0;
  }

  &:focus-visible {
    outline: 2px solid #007ac0;
    outline-offset: 2px;
    border-radius: 2px;
  }

  .anticon {
    font-size: 12px;
  }
`;

type Props = {
  // eslint-disable-next-line no-unused-vars
  onHeightChange?: (height: number) => void;
};

const FeeChangeBanner: FC<Props> = ({ onHeightChange }) => {
  const { ref, height } = useMeasuredHeight<HTMLDivElement>(FEE_CHANGE_BANNER_HEIGHT);

  useEffect(() => {
    onHeightChange?.(height);
  }, [height, onHeightChange]);

  return (
    <BannerBar ref={ref} data-testid="fee-change-banner">
      <BannerInner>
        <BannerText>
          <Emphasis>Upcoming Notice</Emphasis>
          {': New fee effective from '}
          <Emphasis>{FEE_NOTICE_EFFECTIVE_DATE}</Emphasis>
          .
        </BannerText>
        <BannerAction type="button" onClick={scrollToFeeNoticeBand}>
          See new fees
          <DownOutlined aria-hidden="true" />
        </BannerAction>
      </BannerInner>
    </BannerBar>
  );
};

export default FeeChangeBanner;
